import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { AdminLayout } from '@/components/layout';
import { AuthGuard } from '@/components/shared/AuthGuard';

// Auth Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';

// Dashboard
import { DashboardPage } from '@/pages/dashboard/DashboardPage';

// Knowledge Base
import { KnowledgeBasePage } from '@/pages/knowledge-base/KnowledgeBasePage';
import { KBChatPage } from '@/pages/knowledge-base/KBChatPage';
import { ImageSearchPage } from '@/pages/knowledge-base/ImageSearchPage';

// Organization
import { OrganizationPage } from '@/pages/organization/OrganizationPage';
import { DepartmentDetailPage } from '@/pages/organization/DepartmentDetailPage';

// Users
import { UsersPage } from '@/pages/users/UsersPage';

// Tenants (Super Admin)
import { TenantsPage } from '@/pages/tenants';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Placeholder pages - will be implemented later
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="flex h-[50vh] items-center justify-center">
    <div className="text-center">
      <h2 className="text-2xl font-bold">{title}</h2>
      <p className="text-muted-foreground">Coming soon...</p>
    </div>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter basename="/panel">
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <AuthGuard requireAuth={false}>
                  <LoginPage />
                </AuthGuard>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <AuthGuard requireAuth={false}>
                  <ForgotPasswordPage />
                </AuthGuard>
              }
            />

            {/* Protected routes with layout */}
            <Route
              element={
                <AuthGuard requireAuth>
                  <AdminLayout />
                </AuthGuard>
              }
            >
              {/* Dashboard */}
              <Route index element={<DashboardPage />} />

              {/* Super Admin Routes */}
              <Route
                path="tenants"
                element={
                  <AuthGuard superAdminOnly>
                    <TenantsPage />
                  </AuthGuard>
                }
              />
              <Route
                path="tenants/:id"
                element={
                  <AuthGuard superAdminOnly>
                    <PlaceholderPage title="Tenant Details" />
                  </AuthGuard>
                }
              />
              <Route
                path="all-users"
                element={
                  <AuthGuard superAdminOnly>
                    <PlaceholderPage title="All Platform Users" />
                  </AuthGuard>
                }
              />
              <Route
                path="global-settings"
                element={
                  <AuthGuard superAdminOnly>
                    <PlaceholderPage title="Global Settings" />
                  </AuthGuard>
                }
              />
              <Route
                path="system-health"
                element={
                  <AuthGuard superAdminOnly>
                    <PlaceholderPage title="System Health" />
                  </AuthGuard>
                }
              />

              {/* Organization Routes - accessible to all authenticated users */}
              <Route path="organization" element={<OrganizationPage />} />
              <Route path="organization/:id" element={<DepartmentDetailPage />} />

              {/* Users Routes - accessible to all authenticated users */}
              <Route path="users" element={<UsersPage />} />
              <Route
                path="users/:id"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="User Details" />
                  </AuthGuard>
                }
              />
              <Route
                path="roles"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="Roles & Permissions" />
                  </AuthGuard>
                }
              />
              <Route
                path="org-settings"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="Organization Settings" />
                  </AuthGuard>
                }
              />
              <Route
                path="billing"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="Billing & Subscription" />
                  </AuthGuard>
                }
              />
              <Route
                path="audit-logs"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="Audit Logs" />
                  </AuthGuard>
                }
              />

              {/* Common Routes (all authenticated users) */}
              <Route
                path="reports"
                element={<PlaceholderPage title="Reports" />}
              />
              <Route
                path="reports/:id"
                element={<PlaceholderPage title="Report Viewer" />}
              />
              <Route
                path="files"
                element={<PlaceholderPage title="Files" />}
              />
              <Route
                path="knowledge-base"
                element={<KnowledgeBasePage />}
              />
              <Route
                path="kb-chat"
                element={<KBChatPage />}
              />
              <Route
                path="image-search"
                element={<ImageSearchPage />}
              />
              <Route
                path="api-keys"
                element={
                  <AuthGuard tenantAdminOnly>
                    <PlaceholderPage title="API Keys" />
                  </AuthGuard>
                }
              />

              {/* User Settings */}
              <Route path="profile" element={<PlaceholderPage title="Profile" />} />
              <Route path="settings" element={<PlaceholderPage title="Settings" />} />
            </Route>

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
